import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { DialogContent, Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, Button, DialogActions, Field, Checkbox, Label, Textarea, TextareaOnChangeData, Divider } from "@fluentui/react-components";
import { Spinner } from '@fluentui/react-components';
import { CheckmarkCircle24Regular } from "@fluentui/react-icons";
import ContractConceptSelectorComponent from "../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component";
import { ContractConcept } from "../../../models/Entities/Contracts/ContractConcept";

// component functions
import * as Functions from "./VisitConceptLeafModal.Functions";
import { connect } from "react-redux";
import ProviderSelectorComponent from "../../../commons/component/Inputs/ProviderSelector/ProviderSelectorComponent";

// models
import { ContractConceptRelationship } from "../../../models/Entities/Contracts/ContractConceptRelationship";
import { AvailableMedicalService } from "../../../models/Entities/MedicalServices/AvailableMedicalService";

// components
import MedicalServiceSelectorComponent from "../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent";
import { useVisitConceptLeafModalStyles } from "./VisitConceptLeafModal.Styles";


const VisitConceptLeafModal = (props: Functions.Props) => {
  const { t } = useTranslation();
  const styles = useVisitConceptLeafModalStyles();

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hasInitializedForm, setHasInitializedForm] = useState(false);

  // propesties for ContractConceptRelationship

  const [contractConceptIds, setContractConceptIds] = useState<number[]>([]);
  const [selectedConceptNames, setSelectedConceptNames] = useState<string[]>([]);
  const [formConcept, setFormConcept] = useState<string | undefined>(undefined);
  const [fromConceptNameToShow, setFormConceptNameToShow] = useState<string | undefined>(undefined);
  const [isPayable, setIsPayable] = useState(false);
  const [medicalServiceId, setMedicalServiceId] = useState<number | undefined>(undefined);
  const [providerId, setProviderId] = useState<string | undefined>(undefined);
  const [itemNumber, setItemNumber] = useState<number | undefined>(undefined);
  const [formId, setFormId] = useState<number | undefined>(undefined);
  const [formName, setFormName] = useState<string | undefined>(undefined);
  const [isProcedure, setIsProcedure] = useState<boolean>(false);

  const displayFormName = formName ?? props.formName ?? '-';
  const displayFormId = formId ?? props.formId;
  const displayFormConcept = formConcept ?? props.label ?? '-';

  // Find existing relationship for this form concept (case-insensitive via normalized value)
  const normalizedFormConcept = useMemo(() => (props.label ?? '').toString().toUpperCase(), [props.label]);
  const isRealizedLabel = !props.label?.trim() || normalizedFormConcept === 'REALIZED';
  const existingRelationship = useMemo(() => {
    const hasFormContext = props.formId !== undefined || props.formName !== undefined;
    return props.ConceptRelationships?.find(r => {
      if (r.formConcept.toUpperCase() !== normalizedFormConcept) return false;
      if (isRealizedLabel && hasFormContext && props.itemNumber !== undefined) {
        return (
          Functions.matchesFormContext(r, props.formId, props.formName) &&
          r.itemNumber === props.itemNumber
        );
      }
      return true;
    });
  }, [props.ConceptRelationships, normalizedFormConcept, isRealizedLabel, props.formId, props.formName, props.itemNumber]);

  const isRelationshipInSync = (
    relationship: ContractConceptRelationship | undefined,
    conceptId: number | undefined,
    desiredFormConceptNameToShow: string
  ) => {
    if (!relationship) {
      return false;
    }

    return (
      relationship.contractConceptId === conceptId &&
      relationship.contractId === props.ContractId &&
      relationship.formId === (formId ?? props.formId) &&
      relationship.formName === formName &&
      relationship.itemNumber === itemNumber &&
      relationship.formConcept === formConcept &&
      relationship.formConceptNameToShow === desiredFormConceptNameToShow &&
      relationship.isPayable === isPayable &&
      relationship.isProcedure === isProcedure &&
      relationship.medicalServiceId === medicalServiceId &&
      relationship.providerId === providerId
    );
  };

  // events
  const handleSubmit = () => {
    const effectiveFormName = formName ?? props.formName;
    const effectiveFormId = formId ?? props.formId;

    if (!effectiveFormName) {
      setErrorMsg(t('visitConceptLeafModal.formNameRequired', 'Form name is required'));
      return;
    }

    // formId es obligatorio en el modelo. Sin el, el alta viaja incompleta y el
    // update pisaria con undefined el valor ya guardado.
    if (effectiveFormId === undefined || effectiveFormId === null) {
      setErrorMsg(t('visitConceptLeafModal.formIdRequired', 'Form id is required'));
      return;
    }

    if (isPayable && contractConceptIds.length === 0) {
      setErrorMsg(t('visitConceptForm.errors.selectContractConcept', 'You must select a contract concept'));
      return;
    }

    setErrorMsg(undefined);

    const desiredFormConceptNameToShow = fromConceptNameToShow ? fromConceptNameToShow : formConcept!;

    const basePayload = {
      contractId: props.ContractId,
      formId: effectiveFormId,
      itemNumber: itemNumber!,
      formName: effectiveFormName,
      formConcept: formConcept!,
      formConceptNameToShow: desiredFormConceptNameToShow,
      isPayable: isPayable,
      isProcedure: isProcedure,
      medicalServiceId: medicalServiceId!,
      providerId: providerId!,
    };

    const existingRelationships = props.CurrentConceptRelationship || [];

    if (!isPayable) {
      const currentSingleRelationship = existingRelationships[0];
      const hasNoChanges = existingRelationships.length === 1 && isRelationshipInSync(currentSingleRelationship, undefined, desiredFormConceptNameToShow);

      if (hasNoChanges) {
        setOpen(false);
        setSubmitted(false);
        return;
      }

      existingRelationships.forEach((relationship) => {
        if (relationship?.id !== undefined && relationship?.id !== null) {
          props.deleteContractConceptRelationship(relationship.id);
        }
      });

      props.createContractConceptRelationship(props.ContractId, {
        ...basePayload,
        contractConceptId: undefined,
      });

      setSubmitted(true);
      return;
    }

    const existingByConceptId = new Map<number, ContractConceptRelationship>();
    existingRelationships.forEach((relationship) => {
      if (relationship.contractConceptId !== undefined && relationship.contractConceptId !== null) {
        existingByConceptId.set(relationship.contractConceptId, relationship);
      }
    });

    const selectedIdsSet = new Set<number>(contractConceptIds);

    existingRelationships
      .filter((relationship) => {
        const conceptId = relationship.contractConceptId;
        return conceptId !== undefined && conceptId !== null && !selectedIdsSet.has(conceptId);
      })
      .forEach((relationship) => {
        if (relationship?.id !== undefined && relationship?.id !== null) {
          props.deleteContractConceptRelationship(relationship.id);
        }
      });

    const hasPendingChanges =
      existingRelationships.some((relationship) => {
        const conceptId = relationship.contractConceptId;
        return conceptId !== undefined && conceptId !== null && !selectedIdsSet.has(conceptId);
      }) ||
      contractConceptIds.some((conceptId) => !isRelationshipInSync(existingByConceptId.get(conceptId), conceptId, desiredFormConceptNameToShow));

    if (!hasPendingChanges) {
      setOpen(false);
      setSubmitted(false);
      return;
    }

    contractConceptIds.forEach((conceptId) => {
      const existing = existingByConceptId.get(conceptId);

      if (existing) {
        props.updateContractConceptRelationship({
          ...existing,
          ...basePayload,
          contractConceptId: conceptId,
          id: existing.id,
        });
        return;
      }

      props.createContractConceptRelationship(props.ContractId, {
        ...basePayload,
        contractConceptId: conceptId,
      });
    });

    setSubmitted(true);
  };

  const onConceptSelected = (concepts: ContractConcept[]) => {
    const uniqueIds = new Set<number>();
    const uniqueNames = new Set<string>();

    (concepts || []).forEach((concept) => {
      if (concept?.id !== undefined && concept?.id !== null) {
        uniqueIds.add(concept.id);
      }

      const conceptName = concept?.concept?.conceptName;
      const normalizedName = (conceptName || '').trim().toUpperCase();
      if (normalizedName) {
        uniqueNames.add(conceptName.trim());
      }
    });

    setContractConceptIds(Array.from(uniqueIds));
    setSelectedConceptNames(Array.from(uniqueNames));
  };

  useEffect(() => {
    if (open && submitted && (props.successAdding || props.isUpdatedSuccessfully || props.successDeletingOne)) {
      setOpen(false);
      setSubmitted(false);
    }
  }, [open, props.successAdding, props.isUpdatedSuccessfully, props.successDeletingOne, submitted]);

  useEffect(() => {
    if (!open) {
      setHasInitializedForm(false);
      return;
    }

    if (submitted || hasInitializedForm) {
      return;
    }

    setErrorMsg(undefined);
    setFormConcept(props.label);

    if (existingRelationship) {
      setIsPayable(!!existingRelationship.isPayable);
      setIsProcedure(!!existingRelationship.isProcedure);
      setProviderId(existingRelationship.providerId ?? undefined);
      setMedicalServiceId(existingRelationship.medicalServiceId ?? undefined);
      setItemNumber(existingRelationship.itemNumber ?? undefined);
      setFormId(existingRelationship.formId ?? props.formId);
      setFormName(existingRelationship.formName ?? props.formName);
      setFormConceptNameToShow(existingRelationship.formConceptNameToShow ?? undefined);
    } else {
      setIsPayable(false);
      setIsProcedure(false);
      setProviderId(undefined);
      setMedicalServiceId(undefined);
      setItemNumber(props.itemNumber);
      setFormId(props.formId);
      setFormName(props.formName);
      setFormConceptNameToShow(undefined);
    }

    const currentFormRelationships = props.CurrentConceptRelationship || [];
    const selectedIds = currentFormRelationships
      .map((relationship) => relationship.contractConceptId)
      .filter((contractConceptId): contractConceptId is number => contractConceptId !== undefined && contractConceptId !== null);

    setContractConceptIds(Array.from(new Set(selectedIds)));
    setSelectedConceptNames([]);
    setHasInitializedForm(true);
  }, [open, submitted, hasInitializedForm, existingRelationship, props.CurrentConceptRelationship, props.formId, props.formName, props.itemNumber, props.label]);

  const FormFields = (
    <>
      <div className={styles.sectionCard}>
        <Label className={styles.sectionTitle}>{t('visitConceptLeafModal.sections.billing', 'Billing settings')}</Label>
        <Field>
          <Checkbox
            label={t('visitConceptLeafModal.isPayable', 'Is payable')}
            checked={isPayable}
            onChange={(_, data) => setIsPayable(!!data.checked)}
          />
        </Field>
        {isPayable && (
          <div className={styles.fieldStack}>
            <Field validationState={errorMsg ? 'error' : undefined} validationMessage={errorMsg}>
              <ContractConceptSelectorComponent
                ContractId={props.ContractId}
                referralDate={undefined}
                onSelectedContractConcept={
                  (concepts: ContractConcept[]) => onConceptSelected(concepts)
                }
                ContractConceptsSelected={
                  contractConceptIds.length > 0 ? contractConceptIds : undefined
                }
                DistinctByConceptName={true}
                MultiselectEnable={true} />
              {selectedConceptNames.length > 0 && (
                <Label className={styles.selectedConceptsLabel}>
                  {t('visitConceptLeafModal.selectedConceptNames', 'Selected concepts (distinct by name): {{concepts}}', {
                    concepts: selectedConceptNames.join(', '),
                  })}
                </Label>
              )}
            </Field>
            <Field>
              <Label>{t('visitConceptLeafModal.formConceptNameToShow', 'Form concept name to show')}</Label>
              <Textarea
                value={fromConceptNameToShow || formConcept}
                onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => setFormConceptNameToShow(data.value)}
                className={styles.textareaFull}
              />
            </Field>
          </div>
        )}
      </div>

      <div className={styles.sectionCard}>
        <Label className={styles.sectionTitle}>{t('visitConceptLeafModal.sections.procedure', 'Procedure settings')}</Label>
        <Field>
          <Checkbox
            label={t('visitConceptLeafModal.isProcedure', 'Is procedure')}
            checked={isProcedure}
            onChange={(_, data) => setIsProcedure(!!data.checked)}
          />
        </Field>
        {isProcedure && (
          <div className={styles.fieldStack}>
            <Field>
              <MedicalServiceSelectorComponent
                MultiselectEnable={false}
                MedicalServicesSelectedIds={medicalServiceId ? [medicalServiceId] : undefined}
                onSelectedServices={(services: AvailableMedicalService[] | undefined) => setMedicalServiceId(services && services.length > 0 ? services[0]?.id : undefined)}
                noteText={''}
              />
            </Field>
            <Field>
              <ProviderSelectorComponent
                MultiselectEnable={false}
                ProviderSelected={providerId ? [providerId] : undefined}
                onChangeSelection={(providers) => {
                  const firstProviderId = providers && providers.length > 0 ? providers[0]?.person.personIdentifier : undefined;
                  setProviderId(firstProviderId ? String(firstProviderId) : undefined);
                }}
              />
            </Field>
          </div>
        )}
      </div>
    </>
  );

  return (
    <Dialog modalType="alert" open={open} onOpenChange={(event, data) => setOpen(data.open)}>
      <DialogTrigger disableButtonEnhancement>
        {(React.isValidElement(props.triggerAs) && props.triggerAs) || (
          <Button size="small" appearance="subtle">
            {t('visitConceptLeafModal.openForm', 'Open form')}
          </Button>
        )}
      </DialogTrigger>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>{t('visitConceptLeafModal.title', 'Asociar concepto')}</DialogTitle>
          <DialogContent className={styles.contentRoot}>
            <div className={styles.contextCard}>
              <Label className={styles.contextTitle}>{t('visitConceptLeafModal.context', 'Context')}</Label>
              <div className={styles.contextFlow}>
                <div className={styles.contextItem}>
                  <span className={styles.contextKey}>{t('visitConceptLeafModal.formName', 'Form')}</span>
                  <span className={styles.contextValue}>{displayFormName}</span>
                  {displayFormId !== undefined && displayFormId !== null && (
                    <span className={styles.contextBadge} title={t('visitConceptLeafModal.formIdTitle', 'Form id')}>
                      #{displayFormId}
                    </span>
                  )}
                </div>
                <div className={styles.contextConnector} />
                <div className={styles.contextItem}>
                  <span className={styles.contextKey}>{t('visitConceptLeafModal.formConcept', 'Concept')}</span>
                  <span className={styles.contextValue}>{displayFormConcept}</span>
                </div>
              </div>
            </div>
            <div className={styles.statusRow}>
              {props.realized ? (
                <CheckmarkCircle24Regular
                  color="var(--ichtys-success)"
                  title={t('visitConceptLeafModal.realizedLabel', 'Realized')}
                />
              ) : null}
              <span className={styles.statusLabel}>{props.label}</span>
            </div>
            <Divider />
            {FormFields}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button appearance="primary" onClick={handleSubmit} disabled={props.isCreating || props.isUpdating || props.isDeleting} icon={(props.isCreating || props.isUpdating || props.isDeleting) ? <Spinner size="extra-tiny" /> : undefined}>
              {props.isAdding ? t('common.saving') : t('visitConceptLeafModal.confirm', 'Confirm')}
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default connect(
  Functions.mapStateToProps,
  Functions.mapDispatchToProps
)(VisitConceptLeafModal as any);
